import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import es from 'date-fns/locale/es';
import api from '../services/api';

const AlertsPage = () => {
    const { user } = useAuth();
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        type: '',
        severity: '',
        is_reviewed: ''
    });

    const fetchAlerts = async () => {
        try {
            setLoading(true);
            const params = {};
            if (filters.type) params.type = filters.type;
            if (filters.severity) params.severity = filters.severity;
            if (filters.is_reviewed !== '') params.is_reviewed = filters.is_reviewed;

            const response = await api.get('/alerts', { params });
            setAlerts(response.data.alerts || []);
        } catch (error) {
            console.error('Error fetching alerts:', error);
            toast.error('Error cargando alertas');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAlerts();
    }, [filters]);

    const handleMarkAsReviewed = async (alertId) => {
        try {
            await api.patch(`/alerts/${alertId}/review`);
            toast.success('Alerta marcada como revisada');
            fetchAlerts();
        } catch (error) {
            console.error('Error marking alert as reviewed:', error);
            toast.error('Error marcando alerta');
        }
    };

    const handleDelete = async (alertId) => {
        if (!window.confirm('¿Seguro que deseas eliminar esta alerta?')) return;

        try {
            await api.delete(`/alerts/${alertId}`);
            toast.success('Alerta eliminada');
            fetchAlerts();
        } catch (error) {
            console.error('Error deleting alert:', error);
            toast.error('Error eliminando alerta');
        }
    };

    const getSeverityBadge = (severity) => {
        const classes = {
            low: 'bg-blue-100 text-blue-800',
            medium: 'bg-yellow-100 text-yellow-800',
            high: 'bg-orange-100 text-orange-800',
            critical: 'bg-red-100 text-red-800'
        };
        return classes[severity] || 'bg-gray-100 text-gray-800';
    };

    const getTypeBadge = (type) => {
        const labels = {
            empty_invoice: '📭 Factura Vacía',
            missing_sequence: '🔢 Consecutivo Faltante',
            siigo_not_found: '❌ No existe en Siigo',
            other: '⚠️ Otro'
        };
        return labels[type] || type;
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                    <Icons.AlertTriangle className="w-8 h-8 text-orange-500" />
                    Alertas de Auditoría
                </h1>
                <p className="text-gray-600 mt-2">
                    Monitor de seguridad para detección de anomalías en facturas
                </p>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
                    <select
                        value={filters.type}
                        onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="">Todos</option>
                        <option value="empty_invoice">Factura Vacía</option>
                        <option value="missing_sequence">Consecutivo Faltante</option>
                        <option value="siigo_not_found">No existe en Siigo</option>
                        <option value="other">Otro</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Severidad</label>
                    <select
                        value={filters.severity}
                        onChange={(e) => setFilters({ ...filters, severity: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="">Todas</option>
                        <option value="low">Baja</option>
                        <option value="medium">Media</option>
                        <option value="high">Alta</option>
                        <option value="critical">Crítica</option>
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
                    <select
                        value={filters.is_reviewed}
                        onChange={(e) => setFilters({ ...filters, is_reviewed: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="">Todas</option>
                        <option value="false">No Revisadas</option>
                        <option value="true">Revisadas</option>
                    </select>
                </div>

                <div className="flex items-end">
                    <button
                        onClick={() => setFilters({ type: '', severity: '', is_reviewed: 'false' })}
                        className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                    >
                        Ver Pendientes
                    </button>
                </div>
            </div>

            {/* Alerts Table */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center">
                        <Icons.Loader className="w-8 h-8 animate-spin mx-auto text-blue-500" />
                        <p className="mt-4 text-gray-600">Cargando alertas...</p>
                    </div>
                ) : alerts.length === 0 ? (
                    <div className="p-12 text-center">
                        <Icons.CheckCircle className="w-16 h-16 mx-auto text-green-500" />
                        <p className="mt-4 text-xl font-semibold text-gray-700">¡Todo en orden!</p>
                        <p className="text-gray-600">No hay alertas para estos filtros</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tipo
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Factura
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Severidad
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Detalles
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Fecha
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Estado
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {alerts.map((alert) => (
                                    <tr key={alert.id} className={alert.is_reviewed ? 'bg-gray-50' : 'bg-white'}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm font-medium text-gray-900">
                                                {getTypeBadge(alert.alert_type)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="text-sm font-mono font-semibold text-blue-600">
                                                {alert.invoice_number}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getSeverityBadge(alert.severity)}`}>
                                                {alert.severity.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-700 max-w-md">
                                            {alert.details}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                            {format(new Date(alert.created_at), "dd MMM yyyy HH:mm", { locale: es })}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {alert.is_reviewed ? (
                                                <div className="flex items-center gap-2 text-green-600">
                                                    <Icons.CheckCircle className="w-4 h-4" />
                                                    <span className="text-xs">Revisada</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-2 text-orange-600">
                                                    <Icons.AlertCircle className="w-4 h-4" />
                                                    <span className="text-xs">Pendiente</span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex items-center justify-end gap-2">
                                                {!alert.is_reviewed && (
                                                    <button
                                                        onClick={() => handleMarkAsReviewed(alert.id)}
                                                        className="p-2 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                                                        title="Marcar como revisada"
                                                    >
                                                        <Icons.Check className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDelete(alert.id)}
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                                    title="Eliminar"
                                                >
                                                    <Icons.Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Summary Stats */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Total Alertas</p>
                            <p className="text-2xl font-bold text-gray-900">{alerts.length}</p>
                        </div>
                        <Icons.Bell className="w-8 h-8 text-blue-500" />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-orange-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Pendientes</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {alerts.filter(a => !a.is_reviewed).length}
                            </p>
                        </div>
                        <Icons.AlertTriangle className="w-8 h-8 text-orange-500" />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-600">Revisadas</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {alerts.filter(a => a.is_reviewed).length}
                            </p>
                        </div>
                        <Icons.CheckCircle className="w-8 h-8 text-green-500" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AlertsPage;
